import { useAuth } from "@/features/auth/hooks/useAuth";
import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import {
  getDocumentContent,
  type ClipboardDocument,
} from "@/features/clipboard/api/clipboard";
import { useClipboardRoute } from "@/features/clipboard/hooks/useClipboardRoute";
import { useCallback, useRef } from "react";

export const useClipboardOpenPreview = (onError: (message: string) => void) => {
  const { user } = useAuth();
  const { sharedOwnerId } = useClipboardRoute();
  const { setClipboardPreview, setClipboardPreviewLoading } = useTreeViewOutletContext();
  const previewRequestRef = useRef(0);

  return useCallback(
    async (doc: ClipboardDocument, initialView?: "parsed" | "raw") => {
      if (!user) return;
      const requestId = ++previewRequestRef.current;
      setClipboardPreviewLoading(true);
      setClipboardPreview(null);
      try {
        const content = await getDocumentContent(
          doc.id,
          400,
          sharedOwnerId ?? undefined,
        );
        if (previewRequestRef.current === requestId) {
          setClipboardPreview(
            initialView ? { ...content, initial_view: initialView } : content,
          );
        }
      } catch (e) {
        if (previewRequestRef.current === requestId) {
          onError(e instanceof Error ? e.message : "Failed to open document");
        }
      } finally {
        if (previewRequestRef.current === requestId) {
          setClipboardPreviewLoading(false);
        }
      }
    },
    [user, sharedOwnerId, setClipboardPreview, setClipboardPreviewLoading, onError],
  );
};
